import { Loader2, RotateCcw, Zap } from "lucide-react";

import {
  EVENT_LABELS,
  type NextEventResult,
  useDemoQueue,
  useDemoReset,
  useTriggerNextEvent,
} from "@/lib/comms";
import { logger } from "@/lib/logger";

interface DemoControlsProps {
  /** Called with each processed event so the page can spotlight where it landed. */
  onEvent?: (result: NextEventResult) => void;
}

/**
 * Admin demo controls: "Trigger next event" + reset. Lives at the customer
 * list level so the operator sees the event land no matter which customer it
 * belongs to.
 */
export function DemoControls({ onEvent }: DemoControlsProps) {
  const queueQuery = useDemoQueue();
  const trigger = useTriggerNextEvent();
  const reset = useDemoReset();

  const remaining = queueQuery.data?.remaining ?? 0;
  const next = queueQuery.data?.next;

  const handleTrigger = () => {
    logger.debug("[ordering]", "trigger next event", next);
    trigger.mutate(undefined, {
      onSuccess: (result) => onEvent?.(result),
    });
  };
  const handleReset = () => {
    if (window.confirm("Reset all demo data to its initial state?")) {
      logger.debug("[ordering]", "demo reset");
      reset.mutate();
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={handleTrigger}
        disabled={remaining === 0 || trigger.isPending}
        title={
          next
            ? `Next: ${EVENT_LABELS[next.event_type]} · ${next.account_name} · order #${next.order_number}`
            : undefined
        }
        className="bg-jj-red hover:bg-jj-red-dk disabled:bg-wg-04 inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed"
      >
        {trigger.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Zap className="size-4" />
        )}
        {remaining > 0 ? `Trigger next event (${remaining})` : "Queue empty"}
      </button>
      <button
        type="button"
        onClick={handleReset}
        disabled={reset.isPending}
        title="Reset demo data"
        aria-label="Reset demo data"
        className="border-wg-03 text-wg-07 hover:text-jj-red flex size-9 cursor-pointer items-center justify-center rounded-full border bg-white"
      >
        <RotateCcw className="size-4" />
      </button>
      {trigger.isError && (
        <span className="text-risk text-xs font-semibold">
          Couldn’t process the next event — please try again.
        </span>
      )}
    </div>
  );
}
